import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    BotOptionDTO, ConversationStatus,
    TranscriptInListDTO,
    TranscriptTableRequest,
    TranscriptTableResponse
} from "../../../api/generated";
import {botApi, RequestStatus, transcriptApi} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";
import moment from 'moment';
import {SelectOption} from "../../elements/select/SelectDropdown";

export const TRANSCRIPT_TABLE_ITEMS_ON_PAGE = 10

export type TranscriptTableRequestPartial = Partial<TranscriptTableRequest>

interface FetchTranscriptListResult {
    requestKey: string
    response: TranscriptTableResponse
}

export interface TranscriptState {
    botOptions: SelectOption<string>[]
    botOptionsRequestStatus: RequestStatus
    totalCount: number
    transcriptList: TranscriptInListDTO[]
    transcriptListRequestStatus: RequestStatus
    transcriptListRequestId?: string
    transcriptListRequestKey?: string
    transcriptTableRequest: TranscriptTableRequest
}

export const buildDefaultTranscriptTableRequest = (): TranscriptTableRequest => ({
    searchQuery: "",
    startDate: moment().subtract(6, 'days').toISOString(),
    endDate: moment().toISOString(),
    pageNumber: 0,
    pageSize: TRANSCRIPT_TABLE_ITEMS_ON_PAGE,
    chatOutcome: [],
    followUp: [],
    publicBotIdList: [],
    conversationStatusList: [ConversationStatus.CustomerAssisted]
})

const sortedValues = <T>(values?: T[]): T[] => {
    return [...(values ?? [])].sort();
}

export const buildTranscriptTableRequestKey = (request: TranscriptTableRequest): string => {
    return JSON.stringify({
        searchQuery: request.searchQuery ?? "",
        startDate: request.startDate ?? null,
        endDate: request.endDate ?? null,
        chatOutcome: sortedValues(request.chatOutcome),
        followUp: sortedValues(request.followUp),
        publicBotIdList: sortedValues(request.publicBotIdList),
        conversationStatusList: sortedValues(request.conversationStatusList),
        pageNumber: request.pageNumber,
        pageSize: request.pageSize,
        sortBy: request.sortBy ?? null,
        sortDirection: request.sortDirection ?? null
    });
}

const buildInitialState = (): TranscriptState => {
    return {
        botOptions: [] as SelectOption<string>[],
        botOptionsRequestStatus: RequestStatus.NONE,
        totalCount: 0,
        transcriptList: [],
        transcriptListRequestStatus: RequestStatus.NONE,
        transcriptTableRequest: buildDefaultTranscriptTableRequest()
    }
}

const initialState: TranscriptState = buildInitialState()

export const fetchTranscriptList = createAsyncThunk<FetchTranscriptListResult, undefined, { state: RootState }>(
    "transcriptList/fetchTranscriptList",
    (_payload, {getState}) => {
        const transcriptTableRequest = getState().transcriptList.transcriptTableRequest
        const requestKey = buildTranscriptTableRequestKey(transcriptTableRequest);

        return transcriptApi.getTranscriptTable({
            transcriptTableRequest
        }).then(response => {
            return {
                requestKey,
                response
            }
        });
    },
    {
        condition: (_payload, {getState}) => {
            const state = getState().transcriptList;
            const requestKey = buildTranscriptTableRequestKey(state.transcriptTableRequest);
            return state.transcriptListRequestStatus !== RequestStatus.PENDING ||
                state.transcriptListRequestKey !== requestKey;
        }
    }
);

export const fetchBotOptions = createAsyncThunk<Array<BotOptionDTO>, undefined, { state: RootState }>(
    "transcriptList/fetchBotOptions",
    (_payload) => {
        return botApi.getBotOptions();
    },
    {
        condition: (_payload, {getState}) => {
            return getState().transcriptList.botOptionsRequestStatus !== RequestStatus.PENDING;
        }
    }
);

export const transcriptListSlice = createSlice({
    name: 'transcriptList',
    initialState,
    reducers: {
        clearTranscriptListSlice: () => {
            return buildInitialState();
        },
        updateTranscriptTableRequest: (state, {payload}: PayloadAction<TranscriptTableRequestPartial>) => {
            state.transcriptTableRequest = {
                ...state.transcriptTableRequest,
                ...payload,
                pageNumber: payload.pageNumber ?? 0
            }
        },
        resetTranscriptTableRequest: (state) => {
            state.transcriptTableRequest = {
                ...buildDefaultTranscriptTableRequest(),
                publicBotIdList: state.transcriptTableRequest.publicBotIdList,
                sortBy: state.transcriptTableRequest.sortBy,
                sortDirection: state.transcriptTableRequest.sortDirection
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTranscriptList.pending, (state, action) => {
            state.transcriptListRequestStatus = RequestStatus.PENDING;
            state.transcriptListRequestId = action.meta.requestId;
            state.transcriptListRequestKey = buildTranscriptTableRequestKey(state.transcriptTableRequest);
        })
        builder.addCase(fetchTranscriptList.fulfilled, (state, {meta, payload}) => {
            if (state.transcriptListRequestId !== meta.requestId ||
                state.transcriptListRequestKey !== payload.requestKey) return;

            state.transcriptList = payload.response.transcripts ?? [];
            state.totalCount = payload.response.totalCount ?? 0;
            state.transcriptListRequestStatus = RequestStatus.RESOLVED;
            state.transcriptListRequestId = undefined;
            state.transcriptListRequestKey = undefined;
        })
        builder.addCase(fetchTranscriptList.rejected, (state, {meta}) => {
            if (state.transcriptListRequestId !== meta.requestId) return;

            state.transcriptListRequestStatus = RequestStatus.REJECTED;
            state.transcriptListRequestId = undefined;
            state.transcriptListRequestKey = undefined;
        })
        builder.addCase(fetchBotOptions.pending, (state) => {
            state.botOptionsRequestStatus = RequestStatus.PENDING;
        })
        builder.addCase(fetchBotOptions.fulfilled, (state, {payload}) => {
            state.botOptions = payload.map(option => {
                return {
                    value: option.publicId,
                    label: option.siteName
                } as SelectOption<string>
            })
            state.botOptionsRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(fetchBotOptions.rejected, (state) => {
            state.botOptionsRequestStatus = RequestStatus.REJECTED;
        })
    },
});

export const {
    clearTranscriptListSlice,
    updateTranscriptTableRequest,
    resetTranscriptTableRequest
} = transcriptListSlice.actions;

export const transcriptListSelector = (state: RootState) => state.transcriptList.transcriptList;
export const transcriptListTotalCountSelector = (state: RootState): number => state.transcriptList.totalCount;
export const transcriptTableRequestSelector = (state: RootState): TranscriptTableRequest => state.transcriptList.transcriptTableRequest;
export const transcriptListRequestStatusSelector = (state: RootState): RequestStatus => state.transcriptList.transcriptListRequestStatus;
export const transcriptTableBotOptionsSelector = (state: RootState): SelectOption<string>[] => state.transcriptList.botOptions;
export const transcriptTableBotOptionsRequestStatusSelector = (state: RootState): RequestStatus => state.transcriptList.botOptionsRequestStatus;
